import { jsxs, jsx } from "react/jsx-runtime";
import { Box, Paragraph, Flex } from "@zillow/constellation";
import { useRef } from "react";
import { useFormContext } from "react-hook-form";
import { commaSeparateNumber } from "../../utils/zhl-calculator.esm.js";
import LegalPopover from "../CalculatorForm/LegalPopover.esm.js";
import SelectField from "../Fields/SelectField.esm.js";
import TextField from "../Fields/TextField.esm.js";
import { handleFieldChangeEvent, legalPopoverInteractionEventCmsInfo } from "./AmortizationCalculator.Clickstream.esm.js";
import { FieldConfig, useCustomQuotesURL } from "./AmortizationCalculator.utils.esm.js";
import { BLOCK_ID, TOOLTIP, FORM_FIELD } from "./constants.esm.js";
function AmortizationFields() {
  return /* @__PURE__ */ jsxs(Box, { sx: {
    position: "sticky",
    top: "0",
    alignSelf: "flex-start"
  }, children: [
    /* @__PURE__ */ jsx(TextField, { label: "Loan amount", name: FieldConfig.amount.name, leftAdornment: "$", config: FieldConfig.amount.config, autoComplete: "off", format: commaSeparateNumber, tooltip: {
      title: "Loan amount",
      body: /* @__PURE__ */ jsx(Paragraph, { children: "The amount specified in the mortgage contract that the borrower agrees to pay back." }),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: BLOCK_ID,
        block_displayed_txt: "Loan amount"
      }
    }, onChange: () => {
      handleFieldChangeEvent("Loan amount", FORM_FIELD);
    } }),
    /* @__PURE__ */ jsx(SelectField, { options: FieldConfig.term.options, label: "Loan program", name: FieldConfig.term.name, config: FieldConfig.term.config, autoComplete: "off", tooltip: {
      title: "Loan program",
      body: /* @__PURE__ */ jsx(Paragraph, { children: "The length of time you choose to pay off your loan (e.g. 30 years, 20 years, 15 years, etc.)" }),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: BLOCK_ID,
        block_displayed_txt: "Loan program"
      }
    }, onChange: () => {
      handleFieldChangeEvent("Loan program", FORM_FIELD);
    } }),
    /* @__PURE__ */ jsx(TextField, { label: "Interest rate", rightAdornment: "%", name: FieldConfig.rate.name, config: FieldConfig.rate.config, autoComplete: "off", textButtonName: "See current rates", textButtonInteractionEventInfo: {
      block_type_cd: "button",
      block_id: "amortization_calc",
      block_displayed_txt: "See current rates"
    }, textButtonHref: CurrentRatesButtonUrl(), tooltip: {
      title: "Interest rate",
      body: /* @__PURE__ */ jsx(Paragraph, { children: "Representative interest rates are based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics." }),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: BLOCK_ID,
        block_displayed_txt: "Interest rate"
      }
    }, onChange: () => {
      handleFieldChangeEvent("Interest Rate", FORM_FIELD);
    } }),
    /* @__PURE__ */ jsxs(Flex, { display: "flex", alignItems: "flex-end", children: [
      /* @__PURE__ */ jsx(Flex, { marginRight: "xs", flex: "1", children: /* @__PURE__ */ jsx(SelectField, { options: FieldConfig.startMonth.options, label: "Loan start date", name: FieldConfig.startMonth.name, config: FieldConfig.startMonth.config, autoComplete: "off", tooltip: {
        title: "Loan start date",
        body: /* @__PURE__ */ jsx(Paragraph, { children: "The date you make your first mortgage payment." }),
        interactionEventCmsInfo: {
          block_type_cd: TOOLTIP,
          block_id: BLOCK_ID,
          block_displayed_txt: "Loan start date"
        }
      }, onChange: () => {
        handleFieldChangeEvent("Loan start date (month)", FORM_FIELD);
      } }) }),
      /* @__PURE__ */ jsx(Flex, { flex: "1", children: /* @__PURE__ */ jsx(SelectField, { options: FieldConfig.startYear.options, label: "", name: FieldConfig.startYear.name, config: FieldConfig.startYear.config, autoComplete: "off", onChange: () => {
        handleFieldChangeEvent("Loan start date (year)", FORM_FIELD);
      } }) })
    ] }),
    /* @__PURE__ */ jsx(LegalPopover, { triggerText: "Calculator disclaimer", text: "The amortization calculator is intended to be used for educational purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. The estimates are based on information you provide, and does not include other fees and costs that a lender may assess in addition to monthly principal and interest, such as taxes and insurance and the actual payment obligation may be greater. The amortization calculator assumes that you make regular monthly payments on time. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend.", interactionEventCmsInfo: legalPopoverInteractionEventCmsInfo })
  ] });
}
function CurrentRatesButtonUrl() {
  const form = useFormContext();
  const lastValidFields = useRef(null);
  if (lastValidFields.current === null) {
    lastValidFields.current = form.getValues();
  }
  const mortgateRatesUrl = useCustomQuotesURL("mortgage-rates", {
    source: "Z_Amortization_Calc_rates"
  });
  return mortgateRatesUrl;
}
export {
  AmortizationFields as default
};
